import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { cn } from '@/lib/utils'
import type { ScanStatus } from '@/lib/types'

interface StatusFilterProps {
  value?: ScanStatus | undefined
  onChange: (value?: ScanStatus | undefined) => void
  className?: string
}

export function StatusFilter({ value, onChange, className }: StatusFilterProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const statusOptions: Array<{ value: ScanStatus | undefined; label: string }> = [
    { value: undefined, label: t('scan.alleStatussen') },
    { value: 'draft', label: t('scan.status.concept') },
    { value: 'queued', label: t('scan.status.inWachtrij') },
    { value: 'processing', label: t('scan.status.verwerken') },
    { value: 'confirmed', label: t('scan.status.confirmed') },
    { value: 'failed', label: t('scan.status.failed') },
    { value: 'no_messages', label: t('scan.status.geenBerichtenGevonden') },
    { value: 'deleted', label: t('scan.status.deleted') },
  ]

  const selectedOption = statusOptions.find(opt => opt.value === value) || statusOptions[0]

  return (
    <div className={cn('relative', className)}>
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="justify-between min-w-[180px] w-full"
        aria-label={t('scan.status.label')}
      >
        {selectedOption.label}
        <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} aria-hidden="true" />
          <div className="absolute top-full left-0 z-20 mt-1 w-full bg-white border border-input rounded-md shadow-lg">
            {statusOptions.map(option => (
              <button
                key={option.value || 'all'}
                className={cn(
                  'w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                  'first:rounded-t-md last:rounded-b-md',
                  value === option.value && 'bg-accent text-accent-foreground'
                )}
                onClick={() => {
                  onChange(option.value)
                  setIsOpen(false)
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
